import { useState, useEffect } from 'react';
import { ChevronLeft, Save, Container, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SignedLink } from '../common/SignedFile';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';

interface EditTrailerPageProps {
  trailerId: string;
}

export function EditTrailerPage({ trailerId }: EditTrailerPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Trailer Details
  const [unitNumber, setUnitNumber] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [licensePlate, setLicensePlate] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [inspectionExpiration, setInspectionExpiration] = useState('');
  const [registrationExpiration, setRegistrationExpiration] = useState('');
  const [gps, setGps] = useState('NO');
  const [notes, setNotes] = useState('');
  const [owner, setOwner] = useState('');
  const [driverAssigned, setDriverAssigned] = useState('');

  // Basic Info
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [type, setType] = useState('');
  const [year, setYear] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [tireSize, setTireSize] = useState('');
  const [serialNumber, setSerialNumber] = useState('');

  // Tag
  const [tagNumber, setTagNumber] = useState('');
  const [tagState, setTagState] = useState('');

  // Annual DOT
  const [dotIssuedDate, setDotIssuedDate] = useState('');
  const [dotState, setDotState] = useState('');
  const [dotExpirationDate, setDotExpirationDate] = useState('');

  // Insurance
  const [insuranceType, setInsuranceType] = useState('');
  const [insuranceCarrier, setInsuranceCarrier] = useState('');
  const [insuranceExpirationDate, setInsuranceExpirationDate] = useState('');

  // File Uploads
  const [registrationFile, setRegistrationFile] = useState<File | null>(null);
  const [dotFile, setDotFile] = useState<File | null>(null);
  const [existingRegistrationUrl, setExistingRegistrationUrl] = useState('');
  const [existingDotUrl, setExistingDotUrl] = useState('');
  const [deleteRegistration, setDeleteRegistration] = useState(false);
  const [deleteDot, setDeleteDot] = useState(false);

  useEffect(() => {
    loadTrailerData();
  }, [trailerId]);

  const handleDeleteRegistrationFile = async () => {
    if (window.confirm('Are you sure you want to delete the registration file?')) {
      setDeleteRegistration(true);
      setExistingRegistrationUrl('');
      toast.success('Registration file will be deleted when you save');
    }
  };

  const handleDeleteDotFile = async () => {
    if (window.confirm('Are you sure you want to delete the DOT file?')) {
      setDeleteDot(true);
      setExistingDotUrl('');
      toast.success('DOT file will be deleted when you save');
    }
  };

  async function loadTrailerData() {
    try {
      const { data, error } = await supabase
        .from('trailers')
        .select('*')
        .eq('id', trailerId)
        .single();

      if (error) throw error;

      if (data) {
        setUnitNumber(data.unit_number || '');
        setStatus(data.status || 'ACTIVE');
        setLicensePlate(data.license_plate || '');
        setLicenseState(data.license_state || '');
        setInspectionExpiration(data.inspection_expiration || '');
        setRegistrationExpiration(data.registration_expiration || '');
        setGps(data.gps || 'NO');
        setNotes(data.notes || '');
        setOwner(data.owner || '');
        setDriverAssigned(data.driver_assigned || '');
        setVin(data.vin || '');
        setMake(data.make || '');
        setModel(data.model || '');
        setType(data.trailer_type || '');
        setYear(data.year ? String(data.year) : '');
        setLength(data.length ? String(data.length) : '');
        setWidth(data.width ? String(data.width) : '');
        setHeight(data.height ? String(data.height) : '');
        setWeight(data.weight ? String(data.weight) : '');
        setTireSize(data.tire_size || '');
        setSerialNumber(data.serial_number || '');
        setTagNumber(data.tag_number || '');
        setTagState(data.tag_state || '');
        setDotIssuedDate(data.dot_issued_date || '');
        setDotState(data.dot_state || '');
        setDotExpirationDate(data.dot_expiration_date || '');
        setInsuranceType(data.insurance_type || '');
        setInsuranceCarrier(data.insurance_carrier || '');
        setInsuranceExpirationDate(data.insurance_expiration_date || '');
        setExistingRegistrationUrl(data.registration_file_url || '');
        setExistingDotUrl(data.dot_file_url || '');
      }
    } catch (error) {
      console.error('Error loading trailer:', error);
      toast.error('Failed to load trailer data');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Upload files if selected
      let registrationFileUrl = existingRegistrationUrl;
      let dotFileUrl = existingDotUrl;

      // Upload registration file
      if (registrationFile) {
        const fileExt = registrationFile.name.split('.').pop();
        const fileName = `registration_${unitNumber}_${Date.now()}.${fileExt}`;
        const filePath = `trailer-documents/${unitNumber}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, registrationFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        registrationFileUrl = publicUrl;
      }

      // Upload DOT file
      if (dotFile) {
        const fileExt = dotFile.name.split('.').pop();
        const fileName = `dot_${unitNumber}_${Date.now()}.${fileExt}`;
        const filePath = `trailer-documents/${unitNumber}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, dotFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        dotFileUrl = publicUrl;
      }

      const { error } = await supabase.from('trailers').update({
        unit_number: unitNumber,
        status: status,
        license_plate: licensePlate || null,
        license_state: licenseState || null,
        inspection_expiration: inspectionExpiration || null,
        registration_expiration: registrationExpiration || null,
        gps: gps,
        notes: notes || null,
        owner: owner || null,
        driver_assigned: driverAssigned || null,
        vin: vin,
        make: make,
        model: model,
        trailer_type: type,
        year: year ? parseInt(year) : null,
        length: length ? parseFloat(length) : null,
        width: width ? parseFloat(width) : null,
        height: height ? parseFloat(height) : null,
        weight: weight ? parseFloat(weight) : null,
        tire_size: tireSize,
        serial_number: serialNumber,
        tag_number: tagNumber,
        tag_state: tagState,
        dot_issued_date: dotIssuedDate || null,
        dot_state: dotState,
        dot_expiration_date: dotExpirationDate || null,
        insurance_type: insuranceType,
        insurance_carrier: insuranceCarrier,
        insurance_expiration_date: insuranceExpirationDate || null,
        registration_file_url: deleteRegistration ? null : (registrationFileUrl || null),
        dot_file_url: deleteDot ? null : (dotFileUrl || null),
      }).eq('id', trailerId);

      if (error) throw error;

      toast.success('Trailer updated successfully!');
      navigate(`/trailers/${trailerId}`);
    } catch (error) {
      console.error('Error updating trailer:', error);
      toast.error('Failed to update trailer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/trailers/${trailerId}`)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Container className="w-6 h-6 text-purple-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">Edit Trailer {unitNumber}</h1>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(`/trailers/${trailerId}`)}
                className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Trailer Details */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Trailer Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unit Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                  placeholder="e.g., 001"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  GPS
                </label>
                <select
                  value={gps}
                  onChange={(e) => setGps(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="YES">Yes</option>
                  <option value="NO">No</option>
                  <option value="NIJE NAS">NIJE NAS</option>
                  <option value="-">-</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  value={licensePlate}
                  onChange={(e) => setLicensePlate(e.target.value)}
                  placeholder="e.g., STA9970"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  License State
                </label>
                <input
                  type="text"
                  value={licenseState}
                  onChange={(e) => setLicenseState(e.target.value)}
                  placeholder="e.g., FL"
                  maxLength={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Inspection Expiration
                </label>
                <input
                  type="date"
                  value={inspectionExpiration}
                  onChange={(e) => setInspectionExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Registration Expiration
                </label>
                <input
                  type="date"
                  value={registrationExpiration}
                  onChange={(e) => setRegistrationExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Owner
                </label>
                <input
                  type="text"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder="Owner name"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver Assigned
                </label>
                <input
                  type="text"
                  value={driverAssigned}
                  onChange={(e) => setDriverAssigned(e.target.value)}
                  placeholder="Driver name"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ALERT / vracanje / promjena">Alert / vracanje / promjena</option>
                  <option value="UGOVOR TURE">Ugovor Ture</option>
                  <option value="RECOVERY">Recovery</option>
                  <option value="JARDA - ready for new drivers">Jarda - ready for new drivers</option>
                  <option value="SHOP">Shop</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this trailer..."
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Basic Info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  VIN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Make <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Model</label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="dry_van">Dry Van</option>
                  <option value="reefer">Reefer</option>
                  <option value="flatbed">Flatbed</option>
                  <option value="tanker">Tanker</option>
                  <option value="lowboy">Lowboy</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  min="1900"
                  max="2100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Length (ft)</label>
                <input
                  type="number"
                  step="0.1"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  placeholder="e.g., 53"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Width (ft)</label>
                <input
                  type="number"
                  step="0.1"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="e.g., 8.5"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Height (ft)</label>
                <input
                  type="number"
                  step="0.1"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="e.g., 13.5"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Weight (lbs)</label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="e.g., 12000"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tire Size</label>
                <input
                  type="text"
                  value={tireSize}
                  onChange={(e) => setTireSize(e.target.value)}
                  placeholder="e.g., 295/75R22.5"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Serial Number</label>
                <input
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Tag */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Tag</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tag Number</label>
                <input
                  type="text"
                  value={tagNumber}
                  onChange={(e) => setTagNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                <input
                  type="text"
                  value={tagState}
                  onChange={(e) => setTagState(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Annual DOT */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Annual DOT</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Issued Date</label>
                <input
                  type="date"
                  value={dotIssuedDate}
                  onChange={(e) => setDotIssuedDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                <input
                  type="text"
                  value={dotState}
                  onChange={(e) => setDotState(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  maxLength={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expiration Date</label>
                <input
                  type="date"
                  value={dotExpirationDate}
                  onChange={(e) => setDotExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Insurance */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Insurance</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Insurance Type
                </label>
                <select
                  value={insuranceType}
                  onChange={(e) => setInsuranceType(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Select Type</option>
                  <option value="liability">Liability</option>
                  <option value="comprehensive">Comprehensive</option>
                  <option value="collision">Collision</option>
                  <option value="full">Full Coverage</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Insurance Carrier</label>
                <input
                  type="text"
                  value={insuranceCarrier}
                  onChange={(e) => setInsuranceCarrier(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Insurance Expiration Date</label>
                <input
                  type="date"
                  value={insuranceExpirationDate}
                  onChange={(e) => setInsuranceExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Document Uploads</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Registration File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Registration File</label>
                {existingRegistrationUrl && (
                  <div className="mb-2 flex items-center gap-3">
                    <SignedLink
                      stored={existingRegistrationUrl}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View current file
                    </SignedLink>
                    <button
                      type="button"
                      onClick={handleDeleteRegistrationFile}
                      className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                      title="Delete registration file"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setRegistrationFile(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                  {registrationFile && (
                    <div className="text-sm text-slate-600">
                      Selected: {registrationFile.name}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                </div>
              </div>

              {/* DOT File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">DOT File</label>
                {existingDotUrl && (
                  <div className="mb-2 flex items-center gap-3">
                    <SignedLink
                      stored={existingDotUrl}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View current file
                    </SignedLink>
                    <button
                      type="button"
                      onClick={handleDeleteDotFile}
                      className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                      title="Delete DOT file"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setDotFile(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                  {dotFile && (
                    <div className="text-sm text-slate-600">
                      Selected: {dotFile.name}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
